import { useEffect, useState } from 'react';

import { isIosNative } from '@/renderer/utils/isIosNative';

/**
 * Whether the on-screen keyboard is currently up.
 *
 * A boolean, deliberately — unlike its two neighbours, which publish CSS custom
 * properties:
 *
 *  - `useKeyboardLift` publishes `--kb-lift` / `--kb-closed` so a bottom-docked
 *    layer can position itself against the keyboard on the native iOS shell.
 *  - `useVisualViewportHeight` publishes `--support-vh` / `--support-kb-closed`
 *    so the support-chat and CreateRelease surfaces can size themselves.
 *
 * Both are owned by the surface that needs them, mount and unmount with it, and
 * remove their properties on cleanup. The mobile tab bar is not one of those
 * surfaces: it lives for as long as the app's navigation does, on every route,
 * and what it needs is a decision, not a distance — whether to get out of the
 * way, and whether the layout should still reserve room for it. That has to
 * drive React (a class and an inline custom property), so it is state here
 * rather than a fourth globally-written CSS variable that would have to be
 * reconciled against the other three.
 *
 * Two platforms, two signals, for the reason spelled out at length in
 * useKeyboardLift:
 *
 *  - Native iOS runs `Keyboard.resize: 'native'`, so the WebView itself shrinks
 *    and `innerHeight - visualViewport.height` stays ~0 the whole time the
 *    keyboard is up. The viewport gap is useless there; the Keyboard plugin's
 *    `keyboardWillShow` / `keyboardWillHide` fire BEFORE the animation, which is
 *    also the earliest and steadiest moment to react.
 *  - Android's WebView and every browser leave the layout viewport alone and
 *    shrink only the VISUAL viewport, so the gap between the two is the
 *    keyboard. The threshold keeps a collapsing URL bar or a browser toolbar
 *    (tens of px) from reading as a keyboard.
 *
 * SSR-safe: returns false until the effect runs, which is also the right answer
 * for a freshly rendered page.
 */

/** Below this, a visual-viewport shrink is browser chrome, not a keyboard. */
const KEYBOARD_MIN_HEIGHT = 120;

export function useKeyboardOpen(): boolean {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (isIosNative()) {
      let cancelled = false;
      let cleanupKb: (() => void) | undefined;

      void import('@capacitor/keyboard')
        .then(({ Keyboard }) => {
          const willShow = Keyboard.addListener('keyboardWillShow', () => setOpen(true));
          const willHide = Keyboard.addListener('keyboardWillHide', () => setOpen(false));

          cleanupKb = () => {
            void willShow.then((handle) => handle.remove());
            void willHide.then((handle) => handle.remove());
          };

          // Unmounted while the dynamic import was in flight — tear down now, or
          // the listeners outlive the hook.
          if (cancelled) {
            cleanupKb();
          }
        })
        .catch(() => {
          // Plugin unavailable — leave the keyboard reported as down rather than
          // guessing, so the bar stays put instead of flickering.
        });

      return () => {
        cancelled = true;
        cleanupKb?.();
      };
    }

    const viewport = window.visualViewport;

    if (!viewport) {
      return;
    }

    const read = () => {
      setOpen(window.innerHeight - viewport.height > KEYBOARD_MIN_HEIGHT);
    };

    read();
    viewport.addEventListener('resize', read);

    return () => {
      viewport.removeEventListener('resize', read);
    };
  }, []);

  return open;
}
